import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { StickyNote, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Check, X } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

export const ChecklistNotes = ({ checklist, onAdd, onEdit, onDelete }) => {
  const notes = checklist.notes || [];
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const cid = checklist.checklist_id;
  const expanded = open || adding;

  const submitAdd = () => { const t = draft.trim(); if (t) onAdd(cid, t); setDraft(""); setAdding(false); setOpen(true); };
  const startEdit = (n) => { setEditId(n.note_id); setEditText(n.text); };
  const submitEdit = (n) => { const t = editText.trim(); if (t && t !== n.text) onEdit(cid, n.note_id, t); setEditId(null); setEditText(""); };

  return (
    <div className="px-2 pb-2 pt-1.5 border-t border-slate-200" data-testid={`checklist-notes-${cid}`}>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-800"
          data-testid={`notes-toggle-${cid}`}
        >
          {notes.length > 0 ? (expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : <StickyNote className="h-3 w-3" />}
          Notes{notes.length > 0 ? ` (${notes.length})` : ""}
        </button>
        <button
          type="button"
          onClick={() => { setAdding(true); setDraft(""); }}
          className="ml-auto flex items-center gap-0.5 text-[11px] text-[#2563EB] hover:underline"
          data-testid={`notes-add-btn-${cid}`}
        >
          <Plus className="h-3 w-3" />Add note
        </button>
      </div>

      {adding && (
        <div className="mt-1.5" data-testid={`notes-add-form-${cid}`}>
          <Textarea
            autoFocus rows={2}
            className="text-xs bg-white resize-none"
            placeholder="Write a note…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitAdd(); if (e.key === "Escape") { setAdding(false); setDraft(""); } }}
            data-testid={`notes-add-input-${cid}`}
          />
          <div className="flex gap-1.5 mt-1">
            <Button size="sm" className="h-6 px-2 text-[11px] bg-[#2563EB] hover:bg-[#1d4ed8]" disabled={!draft.trim()} onClick={submitAdd} data-testid={`notes-add-save-${cid}`}><Check className="h-3 w-3 mr-1" />Save</Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { setAdding(false); setDraft(""); }}><X className="h-3 w-3" /></Button>
          </div>
        </div>
      )}

      {expanded && notes.length > 0 && (
        <ul className="mt-1.5 space-y-1.5">
          {notes.map((n) => (
            <li key={n.note_id} className="group rounded-md border border-amber-100 bg-amber-50/60 px-2 py-1.5" data-testid={`note-${n.note_id}`}>
              {editId === n.note_id ? (
                <div>
                  <Textarea autoFocus rows={2} className="text-xs bg-white resize-none" value={editText} onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitEdit(n); if (e.key === "Escape") setEditId(null); }} data-testid={`note-edit-input-${n.note_id}`} />
                  <div className="flex gap-1.5 mt-1">
                    <Button size="sm" className="h-6 px-2 text-[11px] bg-[#2563EB] hover:bg-[#1d4ed8]" disabled={!editText.trim()} onClick={() => submitEdit(n)} data-testid={`note-edit-save-${n.note_id}`}><Check className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-1.5">
                  <p className="flex-1 text-xs text-slate-700 whitespace-pre-wrap break-words">{n.text}</p>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                    <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => startEdit(n)} data-testid={`note-edit-${n.note_id}`}><Pencil className="h-3 w-3" /></button>
                    <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => onDelete(cid, n.note_id)} data-testid={`note-delete-${n.note_id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              )}
              {editId !== n.note_id && <div className="text-[10px] text-slate-400 mt-0.5">{fmt(n.updated_at || n.created_at)}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
